/**
 * The admin-side `EditorPort` implementation — wraps the admin products
 * API/hooks (extended in this same task) to satisfy the same `EditorPort`
 * contract the seller adapter satisfies. An admin can create a product for
 * ANY seller and set any status directly (no submit-for-review gate).
 */
import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
  type ReactElement,
} from "react";
import { useTranslation } from "react-i18next";

import { ADMIN_NS, registerAdminI18n } from "@/features/admin/api";
import {
  PRODUCT_STATUSES,
  type AdminProduct,
  type AdminProductCreate,
} from "@/features/admin/products/api";
import {
  useCreateProduct,
  useCreateVariant,
  useDeleteVariant,
  useProduct,
  usePatchProduct,
  useReorderPhotos,
  useSearchSellers,
  useVariants,
} from "@/features/admin/products/hooks";
// SCRUM-197: cross-feature admin import to resolve a hydrated seller's name
// by id (the picker's own search cache only knows sellers the admin has
// actually typed a query for) — same precedent as
// `product-review/api.ts` importing from `products/api.ts`.
import { useSeller } from "@/features/admin/sellers/hooks";
import type {
  EditorCapabilities,
  EditorCreateBody,
  EditorDataBinding,
  EditorPort,
  EditorProduct,
  EditorWriteBody,
  SellerPickerContext,
} from "@/features/product-editor/port";

/**
 * Exported (unlike the seller adapter's module-private twin) so it's
 * directly unit-testable — see `__tests__/editorPort.test.ts`. Pure mapping,
 * no behavior change either way.
 */
export function toEditorProduct(p: AdminProduct): EditorProduct {
  return {
    id: p.id,
    seller_id: p.seller_id,
    category_id: p.category_id,
    name: p.name,
    description: p.description,
    price_kgs: p.price_kgs,
    bulk_price_kgs: p.bulk_price_kgs,
    bulk_threshold: p.bulk_threshold,
    stock_state: p.stock_state,
    status: p.status,
    variant_group_id: p.variant_group_id,
    images: p.images,
    attributes: p.attributes,
    category_ids: p.category_ids,
    primary_category_id: p.primary_category_id,
    // v0048. Listed EXPLICITLY because this mapper names every field — a
    // property added to EditorProduct and not added here arrives as
    // `undefined`, the form renders it blank, and the next Save (which sends
    // the whole form) clears it in the database.
    unit: p.unit,
    brand: p.brand,
    article: p.article,
    formal_name: p.formal_name,
    tags: p.tags,
    folk_names: p.folk_names,
  };
}

/** Debounces a rapidly-changing value by `delay`ms (setTimeout-based,
 * cleared on each change) — mirrors the identical local pattern already used
 * by `ProductListPage.tsx` / `UserListPage.tsx` in this same admin surface. */
function useDebounced<T>(value: T, delay: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(id);
  }, [value, delay]);
  return debounced;
}

const SELLER_PICKER_LISTBOX_ID = "admin-seller-picker-listbox";

/**
 * Seller typeahead — a bounded, scrollable dropdown implementing the
 * WAI-ARIA 1.2 combobox-with-listbox-popup pattern. The raw input value
 * updates instantly for a responsive feel; the query handed to
 * `useSearchSellers` is debounced ~250ms so a fast typist doesn't fire a
 * request per keystroke (TanStack Query also dedupes/caches identical query
 * keys, bounding the practical cost further). Replaces the earlier
 * deliberately-minimal placeholder that rendered results as an unbounded,
 * unstyled flat list inline in the page flow.
 */
function AdminSellerPickerInline({
  sellerId,
  onChange,
  context,
}: {
  sellerId: string | null;
  onChange: (sellerId: string | null) => void;
  /** Optional (not `EditorCapabilities.renderSellerPicker`'s own contract,
   * which the real port always satisfies — see `useAdminEditorPort` below)
   * purely so an older caller supplying only `(sellerId, onChange)` stays
   * type- and behavior-compatible: defaults to "create mode" (no hint, no
   * cancel affordance), matching the pre-SCRUM-197 create-only behavior. */
  context?: SellerPickerContext;
}): ReactElement {
  registerAdminI18n();
  const { t } = useTranslation(ADMIN_NS);
  const reassigning = context?.reassigning ?? false;
  const initialSellerId = context?.initialSellerId ?? null;
  const [rawQuery, setRawQuery] = useState("");
  const debouncedQuery = useDebounced(rawQuery, 250);
  const { data, isFetching } = useSearchSellers(debouncedQuery);
  const sellers = data?.sellers ?? [];
  const selected = sellers.find((s) => s.id === sellerId) ?? null;
  // SCRUM-197: an edit-mode hydrated seller id has no search-cache entry
  // (the admin never typed a query for it) — resolve its name by id as a
  // fallback so the selected view shows a business name, not a raw UUID.
  // `useSeller` is disabled (`enabled: id !== null`) whenever `sellerId` is
  // null, so this is a no-op fetch outside the "selected" branch below.
  const { data: sellerDetail } = useSeller(sellerId);
  const selectedName = selected?.business_name ?? sellerDetail?.business_name ?? sellerId;

  const [open, setOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState<number | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // The results just changed underneath us (new query settled) — any
  // previous keyboard highlight may now point at a stale/out-of-bounds
  // index, so drop it back to "nothing highlighted" rather than risk
  // pointing at the wrong row.
  useEffect(() => {
    setHighlightedIndex(null);
  }, [data]);

  // Click-outside-to-close: only listens while the dropdown is actually
  // open, removed again the moment it closes (or on unmount).
  useEffect(() => {
    if (!open) return undefined;
    function handleDocumentMouseDown(event: MouseEvent): void {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handleDocumentMouseDown);
    return () => document.removeEventListener("mousedown", handleDocumentMouseDown);
  }, [open]);

  const dropdownOpen = open && rawQuery.trim() !== "";
  const highlightedOption =
    highlightedIndex !== null ? (sellers[highlightedIndex] ?? null) : null;

  function selectSeller(id: string): void {
    onChange(id);
    setOpen(false);
  }

  function handleKeyDown(event: KeyboardEvent<HTMLInputElement>): void {
    if (!dropdownOpen) return;
    if (event.key === "ArrowDown") {
      event.preventDefault();
      setHighlightedIndex((idx) => {
        if (sellers.length === 0) return null;
        return idx === null ? 0 : Math.min(idx + 1, sellers.length - 1);
      });
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      setHighlightedIndex((idx) => {
        if (idx === null || sellers.length === 0) return null;
        return Math.max(idx - 1, 0);
      });
    } else if (event.key === "Enter") {
      event.preventDefault();
      if (highlightedIndex !== null && sellers[highlightedIndex]) {
        selectSeller(sellers[highlightedIndex].id);
      }
    } else if (event.key === "Escape") {
      setOpen(false);
    }
  }

  if (sellerId !== null) {
    return (
      <div className="flex items-center gap-2" data-testid="admin-seller-picker">
        <span className="text-sm font-medium text-foreground">
          {t("products.editor.sellerPicker.label")}:
        </span>
        <span data-testid="admin-seller-picker-selected" className="text-sm text-foreground">
          {selectedName}
        </span>
        <button
          type="button"
          onClick={() => onChange(null)}
          data-testid="admin-seller-picker-change"
          className="text-xs font-medium text-primary underline underline-offset-2"
        >
          {t("products.editor.sellerPicker.change")}
        </button>
      </div>
    );
  }

  return (
    <div
      className="flex flex-col gap-1"
      data-testid="admin-seller-picker"
      ref={containerRef}
    >
      <label htmlFor="admin-seller-picker-input" className="text-sm font-medium text-foreground">
        {t("products.editor.sellerPicker.label")}
      </label>
      <div className="relative">
        <input
          id="admin-seller-picker-input"
          data-testid="admin-seller-picker-input"
          role="combobox"
          aria-expanded={dropdownOpen}
          aria-controls={SELLER_PICKER_LISTBOX_ID}
          aria-autocomplete="list"
          aria-activedescendant={
            highlightedOption
              ? `admin-seller-picker-option-${highlightedOption.id}`
              : undefined
          }
          autoComplete="off"
          value={rawQuery}
          onChange={(e) => {
            setRawQuery(e.target.value);
            setOpen(true);
          }}
          onFocus={() => {
            if (rawQuery.trim() !== "") setOpen(true);
          }}
          onKeyDown={handleKeyDown}
          placeholder={t("products.editor.sellerPicker.placeholder")}
          className="min-h-[44px] w-full rounded-[var(--g-radius)] border border-border bg-card px-3 text-sm text-foreground outline-none focus-visible:ring-2 focus-visible:ring-[var(--g-primary)]"
        />
        {dropdownOpen ? (
          <ul
            id={SELLER_PICKER_LISTBOX_ID}
            role="listbox"
            data-testid="admin-seller-picker-results"
            className="absolute left-0 right-0 top-full z-20 mt-1 max-h-64 overflow-y-auto rounded-[var(--g-radius)] border border-[var(--g-hairline)] bg-[var(--g-surface)] shadow-lg"
          >
            {isFetching ? (
              <li className="flex min-h-[44px] items-center px-3 text-xs text-muted-foreground">
                {t("products.editor.sellerPicker.searching")}
              </li>
            ) : sellers.length === 0 ? (
              <li className="flex min-h-[44px] items-center px-3 text-xs text-muted-foreground">
                {t("products.editor.sellerPicker.noResults")}
              </li>
            ) : (
              sellers.map((s, index) => (
                <li
                  key={s.id}
                  id={`admin-seller-picker-option-${s.id}`}
                  data-testid={`admin-seller-picker-option-${s.id}`}
                  role="option"
                  aria-selected={highlightedIndex === index}
                  tabIndex={-1}
                  onMouseEnter={() => setHighlightedIndex(index)}
                  onClick={() => selectSeller(s.id)}
                  className={
                    "flex min-h-[44px] cursor-pointer items-center px-3 text-sm text-foreground " +
                    (highlightedIndex === index
                      ? "bg-[var(--g-hairline)]"
                      : "hover:bg-[var(--g-hairline)]")
                  }
                >
                  {s.business_name}
                </li>
              ))
            )}
          </ul>
        ) : null}
      </div>
      {/* SCRUM-197: only in edit mode (never create) — the admin is either
       * looking at a fresh, not-yet-hydrated picker (harmless: `onChange`
       * with `null` initialSellerId is a no-op) or has clicked «Изменить»
       * away from an already-assigned seller. Either way, search mode in
       * edit context must not strand the form looking seller-less — a
       * cancel affordance restores the seller the product hydrated with. */}
      {reassigning ? (
        <div className="flex flex-col gap-1">
          <p
            data-testid="admin-seller-picker-reassign-hint"
            className="text-xs text-muted-foreground"
          >
            {t("products.editor.sellerPicker.reassignHint")}
          </p>
          <button
            type="button"
            onClick={() => onChange(initialSellerId)}
            data-testid="admin-seller-picker-cancel"
            className="self-start text-xs font-medium text-muted-foreground underline underline-offset-2"
          >
            {t("products.editor.sellerPicker.cancel")}
          </button>
        </div>
      ) : null}
    </div>
  );
}

/** SCRUM-196: `pending_approval` is removed from the admin status control —
 * auto-publish (`resolve_write_status`) rewrites it to `active` server-side
 * on every write, so offering it as a settable option was a lie the server
 * always overruled. `PRODUCT_STATUSES` itself (the frozen 6-value CHECK
 * mirror, `products/api.ts:28-35`) stays untouched — this filter is
 * presentation only. */
const SELECTABLE_STATUSES = PRODUCT_STATUSES.filter((s) => s !== "pending_approval");

/** Plain status `<select>` — admin can set ANY selectable status directly
 * (including `active`), unlike the seller's gated submit-for-review flow.
 * Reuses the ALREADY-SHIPPED `products.status.*` i18n keys (the old admin
 * product edit form used the same keys — no new translations needed for the
 * options). */
function AdminStatusSelect({
  value,
  onChange,
}: {
  value: string;
  onChange: (value: string) => void;
}): ReactElement {
  registerAdminI18n();
  const { t } = useTranslation(ADMIN_NS);
  return (
    <select
      data-testid="editor-status-select"
      aria-label={t("products.editor.statusControl.label")}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="min-h-[44px] rounded-[var(--g-radius)] border border-border bg-card px-3 text-sm text-foreground outline-none"
    >
      {SELECTABLE_STATUSES.map((s) => (
        <option key={s} value={s}>
          {t(`products.status.${s}`)}
        </option>
      ))}
      {/* Legacy-row guard: a product persisted as `pending_approval` before
       * this control stopped offering it must still render truthfully — a
       * controlled <select> with no option matching its `value` renders
       * blank/mismatched. Shown disabled so the admin can SEE the current
       * state but cannot re-select it (SCRUM-196). */}
      {value === "pending_approval" ? (
        <option value="pending_approval" disabled>
          {t("products.status.pending_approval")}
        </option>
      ) : null}
    </select>
  );
}

export function useAdminEditorPort(): EditorPort {
  registerAdminI18n();
  const { t } = useTranslation(ADMIN_NS);

  const capabilities: EditorCapabilities = {
    mode: "admin",
    showSellerPicker: true,
    renderSellerPicker: (sellerId, onChange, context) => (
      <AdminSellerPickerInline sellerId={sellerId} onChange={onChange} context={context} />
    ),
    statusControl: "direct",
    // Auto-publish (REQ-PRD-§11.14-F): an admin "Add product" defaults to live.
    defaultCreateStatus: "active",
    renderStatusControl: (value, onChange) => (
      <AdminStatusSelect value={value} onChange={onChange} />
    ),
    showSuggestTriggers: false,
    showVariants: true,
    forbiddenRedirectTo: undefined,
    productPathAfterCreate: (id) => `/admin/products/${id}/edit`,
    listPath: "/admin/products",
    pageTitle: (isNew) =>
      isNew ? t("products.editor.newTitle") : t("products.editor.editTitle"),
  };

  function useData(productId: string | null): EditorDataBinding {
    const rawQuery = useProduct(productId);
    const rawCreate = useCreateProduct();
    const rawPatch = usePatchProduct();
    const rawReorder = useReorderPhotos();

    // Memoized on `rawQuery.data` (TanStack Query keeps that reference
    // STABLE across re-renders until the query actually refetches/updates —
    // only `toEditorProduct`'s OWN wrapping needs stabilising). Without this,
    // `productQuery.data` is a brand-new object on every render regardless of
    // whether the underlying product changed, and `ProductEditor.tsx`'s
    // hydrate-on-load effect (keyed on `productQuery.data` by reference)
    // refires on literally every render — each firing calls `setPhotos(...)`
    // with a fresh (referentially different) array, forcing another render,
    // forever. Confirmed via a real TDD run: editing any existing product
    // hung indefinitely (React's own "Maximum update depth exceeded" +
    // repeated "not wrapped in act()" warnings) until this memoization was
    // added. Flagged in the task report as a deviation from the brief's
    // exact snippet — the brief's un-memoized form (mirrored from the F2
    // seller adapter) is unsafe the moment a real GET-by-id query is wired
    // in, which none of F1's stub-port tests or F2's own quick-settling
    // tests happened to hold the component mounted long enough to observe.
    const editorProduct = useMemo(
      () => (rawQuery.data ? toEditorProduct(rawQuery.data) : undefined),
      [rawQuery.data],
    );

    return {
      productQuery: {
        data: editorProduct,
        error: rawQuery.error,
      },
      create: {
        ...rawCreate,
        mutate: (body: EditorCreateBody, options) =>
          // `AdminProductCreate.seller_id` is REQUIRED (brief 1c — the admin
          // endpoint always needs an explicit seller), but the shared
          // `EditorCreateBody.seller_id` is optional (the seller adapter
          // never sets it at all). Genuine TS error the brief's snippet
          // didn't anticipate — NOT the documented 4-param callback gotcha.
          // The cast is safe: `ProductEditor.tsx::submitWith` refuses to call
          // `create.mutate` at all while `capabilities.showSellerPicker &&
          // productId === null && sellerId === null` (and `submitDisabled`
          // keeps the Save button disabled the whole time), so by the time
          // this admin-mode `create.mutate` actually fires, `body.seller_id`
          // is always a real string. Same "documented safe cast" precedent
          // as the `as EditorDataBinding[...]` casts below.
          rawCreate.mutate(body as AdminProductCreate, {
            // Installed @tanstack/react-query (5.100.11) `MutateOptions.onSuccess`
            // takes 4 params (`data, variables, onMutateResult, context`), not the
            // 3-param shape the F3 brief's snippet assumed — matches the same
            // gotcha F2 already documented; forward all four.
            onSuccess: (data, variables, onMutateResult, context) =>
              options?.onSuccess?.(
                toEditorProduct(data),
                variables,
                onMutateResult,
                context,
              ),
            onError: options?.onError,
          }),
      } as EditorDataBinding["create"],
      patch: {
        ...rawPatch,
        mutate: (vars: { id: string; body: EditorWriteBody }, options) =>
          rawPatch.mutate(vars, {
            onSuccess: (data, variables, onMutateResult, context) =>
              options?.onSuccess?.(
                toEditorProduct(data),
                variables,
                onMutateResult,
                context,
              ),
            onError: options?.onError,
          }),
      } as EditorDataBinding["patch"],
      reorder: {
        ...rawReorder,
        mutate: (vars, options) =>
          rawReorder.mutate(vars, {
            onSuccess: (data, variables, onMutateResult, context) =>
              options?.onSuccess?.(
                toEditorProduct(data),
                variables,
                onMutateResult,
                context,
              ),
            onError: options?.onError,
          }),
      } as EditorDataBinding["reorder"],
    };
  }

  return {
    capabilities,
    useData,
    variants: {
      useList: useVariants,
      useCreate: useCreateVariant,
      useDelete: useDeleteVariant,
    },
  };
}
